import { keyBy } from 'es-toolkit';
import { Dispatch, Middleware } from 'redux';
import { createSelector } from '@reduxjs/toolkit';

import { Component, RelativeBearingsGeometry, RelativeMemberGeometry } from '@paragon/contract-library';
import { enqueueWarning } from '@paragon/notification-tools';
import { calculateLine, calculateRelativeMemberGeometry, findAndCalculateLine } from '@paragon/relative-geometry';

import { RootState } from '..';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getActiveComponent } from '../selectors/getActiveComponentSelector';
import {
  getRelativeBearingGeometriesForActiveComponentDesign,
  getRelativeMemberGeometriesForActiveComponentDesign,
} from '../selectors/getRelativeGeometriesForActiveComponentDesignSelector';

const isReferenceLineValid = (geometry: RelativeMemberGeometry, component: Component) =>
  !geometry.lineGuid || !!findAndCalculateLine(geometry.lineGuid, component);

const getMemberGeometriesWithInvalidReferenceLines = createSelector(
  getActiveComponent,
  getRelativeMemberGeometriesForActiveComponentDesign,
  (component, relativeMemberGeometries) =>
    component &&
    relativeMemberGeometries &&
    relativeMemberGeometries.filter((geometry) => !isReferenceLineValid(geometry, component)),
);

const areCutLinesValid = (geometry: RelativeMemberGeometry, component: Component) =>
  calculateRelativeMemberGeometry(geometry, component) ||
  geometry.baseCutGuids.concat(geometry.endCutGuids).every((guid) => !!findAndCalculateLine(guid, component));

const getMemberGeometriesWithInvalidCutLines = createSelector(
  getActiveComponent,
  getRelativeMemberGeometriesForActiveComponentDesign,
  (component, relativeMemberGeometries) =>
    component &&
    relativeMemberGeometries &&
    relativeMemberGeometries.filter((geometry) => !areCutLinesValid(geometry, component)),
);

const doCutLinesCrossMember = (geometry: RelativeMemberGeometry, component: Component) => {
  if (calculateRelativeMemberGeometry(geometry, component) || !geometry.lineGuid) {
    return true;
  }
  const line = findAndCalculateLine(geometry.lineGuid, component);
  if (!line) {
    return true;
  }
  const doesSomeCutLineCrossMember = (cutLines: readonly string[]) =>
    !cutLines.every((guid) => {
      const cutLine = findAndCalculateLine(guid, component);
      return !cutLine || cutLine.isParallelTo(line);
    });
  return doesSomeCutLineCrossMember(geometry.baseCutGuids) && doesSomeCutLineCrossMember(geometry.endCutGuids);
};

const areCutLinesMidline = (geometry: RelativeMemberGeometry, component: Component) =>
  !calculateRelativeMemberGeometry(geometry, component) &&
  !geometry.baseCutGuids.concat(geometry.endCutGuids).every((guid) => {
    const cutLine = findAndCalculateLine(guid, component);
    const { referenceLines } = component;
    if (!referenceLines) throw new Error('Reference lines should exist on the component by this point.');
    const midline = calculateLine(
      referenceLines.find(
        (line) =>
          line.lineType === 'Member' && line.lineLocation === 'Center' && line.memberGeometryGuid === geometry.guid,
      )!,
      component,
    );
    return !cutLine || !midline || !cutLine.equals(midline);
  });

const getMemberGeometriesWithMidlineCutLines = createSelector(
  getActiveComponent,
  getRelativeMemberGeometriesForActiveComponentDesign,
  (component, relativeMemberGeometries) =>
    component &&
    relativeMemberGeometries &&
    relativeMemberGeometries.filter(
      (geometry) => doCutLinesCrossMember(geometry, component) && areCutLinesMidline(geometry, component),
    ),
);

const areBearingDefinitionLinesParallel = (relativeBearingGeometry: RelativeBearingsGeometry, component: Component) => {
  if (relativeBearingGeometry.bearingGeometryType === 'Bearing') {
    const { lineGuid, bearingLineGuid } = relativeBearingGeometry;
    const definitionLine = findAndCalculateLine(lineGuid, component);
    const bearingLine = findAndCalculateLine(bearingLineGuid, component);
    return definitionLine && bearingLine && definitionLine.isParallelTo(bearingLine);
  }

  const { leftSideLineGuid, rightSideLineGuid, bearingLineGuid } = relativeBearingGeometry;
  const leftSideLine = findAndCalculateLine(leftSideLineGuid, component);
  const rightSideLine = findAndCalculateLine(rightSideLineGuid, component);
  const bearingLine = findAndCalculateLine(bearingLineGuid, component);
  return (
    leftSideLine &&
    rightSideLine &&
    bearingLine &&
    (leftSideLine.isParallelTo(bearingLine) || rightSideLine.isParallelTo(bearingLine))
  );
};

const getBearingGeometriesWithParallelDefinitionLines = createSelector(
  getActiveComponent,
  getRelativeBearingGeometriesForActiveComponentDesign,
  (component, relativeBearingGeometries) =>
    component &&
    relativeBearingGeometries &&
    relativeBearingGeometries.filter((geometry) => areBearingDefinitionLinesParallel(geometry, component)),
);

const getMemberGeometriesWithInvalidCuts = createSelector(
  getActiveComponent,
  getRelativeMemberGeometriesForActiveComponentDesign,
  (component, relativeMemberGeometries) =>
    component &&
    relativeMemberGeometries &&
    relativeMemberGeometries.filter(
      (geometry) =>
        geometry.lineGuid &&
        isReferenceLineValid(geometry, component) &&
        areCutLinesValid(geometry, component) &&
        !areCutLinesMidline(geometry, component) &&
        doCutLinesCrossMember(geometry, component) &&
        !calculateRelativeMemberGeometry(geometry, component),
    ),
);

const formatList = (items: string[]) => {
  if (items.length <= 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

interface GeometryProblemType {
  readonly getGeometries: (
    state: RootState,
  ) => (RelativeMemberGeometry | RelativeBearingsGeometry)[] | undefined | null;
  readonly getMessage: (names: string[]) => string;
}

const geometryProblemTypes: readonly GeometryProblemType[] = [
  {
    getGeometries: getMemberGeometriesWithInvalidReferenceLines,
    getMessage: (memberNames: string[]) => {
      const s = memberNames.length === 1 ? '' : 's';
      return `The reference line${s} for member${s} ${formatList(memberNames)} ${
        memberNames.length === 1 ? 'is' : 'are'
      } defined in an impossible way.`;
    },
  },
  {
    getGeometries: getMemberGeometriesWithInvalidCutLines,
    getMessage: (memberNames: string[]) => {
      const s = memberNames.length === 1 ? '' : 's';
      return `One or more cut lines for member${s} ${formatList(memberNames)} are defined in an impossible way.`;
    },
  },
  {
    getGeometries: getMemberGeometriesWithMidlineCutLines,
    getMessage: (memberNames: string[]) => {
      const s = memberNames.length === 1 ? '' : 's';
      return `Member${s} ${formatList(memberNames)} could not be cut because ${
        memberNames.length === 1 ? 'one of the cut lines' : 'each has a cut line that'
      } is the member's midline.`;
    },
  },
  {
    getGeometries: getMemberGeometriesWithInvalidCuts,
    getMessage: (memberNames: string[]) => {
      const s = memberNames.length === 1 ? '' : 's';
      return `Member${s} ${formatList(
        memberNames,
      )} could not be cut because the base and end cuts must be on opposite ends of a member.`;
    },
  },
  {
    getGeometries: getBearingGeometriesWithParallelDefinitionLines,
    getMessage: (bearingNames: string[]) => {
      const s = bearingNames.length === 1 ? '' : 's';
      return `The positions of bearing${s} ${formatList(
        bearingNames,
      )} could not be calculated because their defining reference lines are parallel.`;
    },
  },
];

export const invalidGeometryMiddleware: Middleware<{}, RootState, Dispatch> = (store) => (next) => (action) => {
  const oldState = store.getState();
  next(action);

  const scanForInvalidGeometries = () => {
    const newState = store.getState();
    if (getActiveComponentDesign(oldState)?.guid !== getActiveComponentDesign(newState)?.guid) {
      return;
    }

    const oldMemberGeometriesByProblemType = geometryProblemTypes.map(({ getGeometries }) =>
      keyBy(getGeometries(oldState) || [], (geometry) => geometry.guid),
    );

    for (let index = 0; index < geometryProblemTypes.length; index += 1) {
      const { getGeometries, getMessage } = geometryProblemTypes[index];
      const geometriesWithProblem = (getGeometries(newState) || []).filter(
        (geometry) => !oldMemberGeometriesByProblemType[index][geometry.guid],
      );
      if (geometriesWithProblem.length) {
        store.dispatch(
          enqueueWarning(
            getMessage(
              geometriesWithProblem.map((geometry) =>
                'memberName' in geometry ? geometry.memberName : geometry.bearingName,
              ),
            ),
          ),
        );
      }
    }
  };

  if (window.requestIdleCallback) {
    window.requestIdleCallback(scanForInvalidGeometries, { timeout: 500 });
  } else {
    setTimeout(scanForInvalidGeometries, 500);
  }
};
